import type { Team } from '@/constants/crm'

import { useMemo } from 'react'
import Avatar from '@/components/ui/avatar'
import StatCard from '@/components/generals/stat-card'
import SegmentBar from '@/components/generals/segment-bar'
import { SectionHeading } from '@/components/generals/view-parts'
import { ownerByName } from '@/constants/companies'
import { formatCompactMoney, formatMoney } from '@/lib/companies'
import { isOpenDeal, weightedValue } from '@/lib/crm'
import { quarterOf } from '@/lib/dates'
import { useCrmStore } from '@/stores/crm-store-provider'

interface TeamViewProps {
    team: Team
}

export default function TeamView({ team }: TeamViewProps) {
    const today = useCrmStore(state => state.today)
    const companies = useCrmStore(state => state.companies)
    const deals = useCrmStore(state => state.deals)
    const activities = useCrmStore(state => state.activities)
    const openProfile = useCrmStore(state => state.openProfile)
    const quarter = quarterOf(today)

    const members = useMemo(() => team.members.map(name => {
        const owner = ownerByName(name)
        const accounts = companies.filter(company => company.owner === name)
        const memberDeals = deals.filter(deal => deal.owner === name)
        const open = memberDeals.filter(isOpenDeal)
        const wonThisQuarter = memberDeals
            .filter(deal => deal.stage === 'Closed Won' && deal.closeDate >= quarter.start && deal.closeDate <= quarter.end)
            .reduce((sum, deal) => sum + deal.value, 0)
        const openActivities = activities.filter(activity => activity.owner === name && !activity.completed).length
        const winRate = accounts.length
            ? Math.round(accounts.reduce((sum, company) => sum + company.winProbability, 0) / accounts.length)
            : 0

        return {
            owner,
            accounts: accounts.length,
            openDeals: open.length,
            pipeline: open.reduce((sum, deal) => sum + deal.value, 0),
            weighted: open.reduce((sum, deal) => sum + weightedValue(deal), 0),
            wonThisQuarter,
            openActivities,
            winRate
        }
    }).sort((a, b) => b.pipeline - a.pipeline), [team, companies, deals, activities, quarter.start, quarter.end])

    const totals = members.reduce((sum, member) => ({
        accounts: sum.accounts + member.accounts,
        openDeals: sum.openDeals + member.openDeals,
        pipeline: sum.pipeline + member.pipeline,
        wonThisQuarter: sum.wonThisQuarter + member.wonThisQuarter
    }), { accounts: 0, openDeals: 0, pipeline: 0, wonThisQuarter: 0 })

    return (
        <div className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4'>
            <p className='text-soft max-w-[40em]'>
                {team.description}
            </p>

            <div className='grid grid-cols-2 gap-2 lg:grid-cols-4'>
                <StatCard label="Accounts owned" value={totals.accounts} hint={`${members.length} people on the team`} />
                <StatCard label="Open deals" value={totals.openDeals} />
                <StatCard label="Open pipeline" value={formatCompactMoney(totals.pipeline)} hint={`$${formatMoney(totals.pipeline)}`} />
                <StatCard label={`Won in ${quarter.label}`} value={formatCompactMoney(totals.wonThisQuarter)} />
            </div>

            <section className='flex flex-col gap-3'>
                <SectionHeading title="Members" count={members.length} />
                <ul className='grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3'>
                    {members.map(member => (
                        <li key={member.owner.name}>
                            <button
                                type='button'
                                onClick={() => openProfile(member.owner.name)}
                                aria-label={`Open ${member.owner.name} profile`}
                                className='bg-card hover:bg-[#21242a] focus-visible:ring-ring/60 flex h-full w-full cursor-pointer flex-col gap-4 rounded-xl p-4 text-left shadow-[0px_4px_4px_0px_rgba(15,15,15,0.24),0px_0px_0px_1px_#0e0e0e,inset_0px_1px_0px_0px_rgba(255,255,255,0.06),inset_0px_0px_0px_1px_rgba(255,255,255,0.06)] outline-none transition-colors duration-150 focus-visible:ring-2'
                            >
                                <span className='flex items-center gap-3'>
                                    <Avatar src={member.owner.avatar} alt='' className='size-10' />
                                    <span className='flex min-w-0 flex-col gap-1.5'>
                                        <span className='lead-style truncate font-medium'>{member.owner.name}</span>
                                        <span className='caption-style text-soft truncate'>{member.owner.role}</span>
                                    </span>
                                </span>

                                <span className='grid grid-cols-3 gap-2'>
                                    {[
                                        { label: "Accounts", value: String(member.accounts) },
                                        { label: "Open deals", value: String(member.openDeals) },
                                        { label: "Open tasks", value: String(member.openActivities) }
                                    ].map(stat => (
                                        <span key={stat.label} className='border-line-strong flex flex-col gap-2 rounded-lg border p-2.5'>
                                            <span className='caption-style text-subtle'>{stat.label}</span>
                                            <span className='lead-style tabular-nums'>{stat.value}</span>
                                        </span>
                                    ))}
                                </span>

                                <span className='flex flex-col gap-2'>
                                    <span className='caption-style flex items-center justify-between gap-2'>
                                        <span className='text-soft'>Open pipeline</span>
                                        <span className='tabular-nums'>${formatMoney(member.pipeline)}</span>
                                    </span>
                                    <span className='caption-style flex items-center justify-between gap-2'>
                                        <span className='text-soft'>Weighted</span>
                                        <span className='tabular-nums'>${formatMoney(member.weighted)}</span>
                                    </span>
                                    <span className='caption-style flex items-center justify-between gap-2'>
                                        <span className='text-soft'>Avg. account win probability</span>
                                        <span className='tabular-nums'>{member.winRate}%</span>
                                    </span>
                                    <SegmentBar percent={member.winRate} segments={40} className='mt-1 h-3 w-full border border-white/4 px-px' segmentClassName='h-2' trackClassName='bg-white/8' />
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    )
}
